import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, 
  Anchor, 
  Shield, 
  Calculator, 
  Scale, 
  FileText, 
  Users, 
  AlertTriangle,
  BookOpen,
  ArrowRight,
  HardHat,
  Flame,
  Heart,
  Wrench
} from 'lucide-react';

// Map of paths to their display info
const linkConfig = {
  '/car-crashes': {
    title: 'Car Crash Claims',
    description: 'Washington car accident injury guide',
    icon: Car,
    color: 'bg-rose-100 text-rose-600 border-rose-200'
  },
  '/compensation-estimator': {
    title: 'Compensation Estimator',
    description: 'Calculate your potential case value',
    icon: Calculator,
    color: 'bg-teal-100 text-teal-600 border-teal-200'
  },
  '/first-responder-crashes': {
    title: 'First Responder Crashes',
    description: 'Claims for police, fire, EMS & more',
    icon: Shield,
    color: 'bg-blue-100 text-blue-600 border-blue-200'
  },
  '/maritime-injuries': {
    title: 'Maritime Injuries',
    description: 'Jones Act & maritime worker claims',
    icon: Anchor,
    color: 'bg-cyan-100 text-cyan-600 border-cyan-200'
  },
  '/pricing': {
    title: 'Pricing & Costs',
    description: 'Transparent fee information',
    icon: FileText,
    color: 'bg-amber-100 text-amber-600 border-amber-200'
  },
  '/faqs': {
    title: 'FAQs',
    description: 'Common questions answered',
    icon: BookOpen,
    color: 'bg-purple-100 text-purple-600 border-purple-200'
  },
  '/slip-and-fall': {
    title: 'Slip & Fall Claims',
    description: 'Premises liability injuries',
    icon: AlertTriangle,
    color: 'bg-orange-100 text-orange-600 border-orange-200'
  },
  '/about': {
    title: 'About Amy',
    description: 'Learn about your attorney',
    icon: Users,
    color: 'bg-pink-100 text-pink-600 border-pink-200'
  },
  // Blog post links
  '/blog/first-responder-injury-rights-washington': {
    title: 'First Responder Rights',
    description: 'Third-party claims for first responders',
    icon: Shield,
    color: 'bg-blue-100 text-blue-600 border-blue-200'
  },
  '/blog/firefighter-injury-claims-washington': {
    title: 'Firefighter Injuries',
    description: 'Claims for fire & rescue workers',
    icon: Flame,
    color: 'bg-red-100 text-red-600 border-red-200'
  },
  '/blog/paramedic-emt-injury-claims-washington': {
    title: 'Paramedic & EMT Claims',
    description: 'Rights for EMS workers',
    icon: Heart,
    color: 'bg-emerald-100 text-emerald-600 border-emerald-200'
  },
  '/blog/wsdot-highway-worker-injury-claims-washington': {
    title: 'WSDOT Worker Claims',
    description: 'Highway worker injury rights',
    icon: HardHat,
    color: 'bg-amber-100 text-amber-600 border-amber-200'
  },
  '/blog/public-works-employee-injury-claims-washington': {
    title: 'Public Works Claims',
    description: 'City & county worker rights',
    icon: Wrench,
    color: 'bg-slate-100 text-slate-600 border-slate-200'
  },
  '/blog/washington-comparative-fault-law-injury-settlement': {
    title: 'Comparative Fault Law',
    description: 'How shared fault affects your settlement',
    icon: Scale,
    color: 'bg-indigo-100 text-indigo-600 border-indigo-200'
  },
  '/blog/why-statutes-of-limitations-exist-washington-personal-injury': {
    title: 'Statute of Limitations',
    description: 'Filing deadlines explained',
    icon: FileText,
    color: 'bg-rose-100 text-rose-600 border-rose-200'
  },
};

// Default config for unknown links
const defaultConfig = {
  title: 'Related Article',
  description: 'Learn more',
  icon: BookOpen,
  color: 'bg-slate-100 text-slate-600 border-slate-200'
};

const RelatedArticlesCards = ({ links = [] }) => {
  if (!links || links.length === 0) return null;

  return (
    <div className="my-12 p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200">
      <h3 className="font-heading text-lg font-bold text-secondary mb-4 flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-primary" />
        Related Resources
      </h3>
      <div className={`grid gap-4 ${links.length === 1 ? 'grid-cols-1' : links.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {links.map((link, index) => {
          const config = linkConfig[link.path] || { ...defaultConfig, title: link.text };
          const Icon = config.icon;
          
          return (
            <Link
              key={index}
              to={link.path}
              className="group flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 hover:border-primary hover:shadow-md transition-all duration-300"
            >
              <div className={`p-3 rounded-lg ${config.color} border group-hover:scale-110 transition-transform`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-secondary group-hover:text-primary transition-colors text-sm">
                  {config.title}
                </h4>
                <p className="text-slate-500 text-xs mt-1 line-clamp-2">
                  {config.description}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

// Helper function to extract related links from HTML content
export const extractRelatedLinks = (htmlContent) => {
  if (!htmlContent) return [];
  
  // Match patterns like: Related: <a href="/path">Text</a> | <a href="/path2">Text2</a>
  // Or: (Internal links: <a href="/path">Text</a> | <a href="/path2">Text2</a>)
  const relatedPattern = /<em>(?:Related|Internal(?: links)?):?\s*(.*?)<\/em>/gi;
  const linkPattern = /<a[^>]*href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi;
  
  const links = [];
  let relatedMatch;
  
  while ((relatedMatch = relatedPattern.exec(htmlContent)) !== null) {
    const relatedSection = relatedMatch[1];
    let linkMatch;
    
    while ((linkMatch = linkPattern.exec(relatedSection)) !== null) {
      const path = linkMatch[1];
      const text = linkMatch[2].trim();
      
      // Only include internal links
      if (path.startsWith('/')) {
        links.push({ path, text });
      }
    }
  }
  
  return links;
};

// Helper function to remove the related links section from content
export const removeRelatedLinksFromContent = (htmlContent) => {
  if (!htmlContent) return htmlContent;
  
  // Remove the entire <p><em>Related:...</em></p> or similar patterns
  return htmlContent
    .replace(/<p>\s*<em>(?:Related|Internal(?: links)?):?\s*.*?<\/em>\s*<\/p>/gi, '')
    .replace(/<p>\s*\((?:Internal(?: links)?):?\s*.*?\)\s*<\/p>/gi, '')
    .replace(/<em>(?:Related|Internal(?: links)?):?\s*<a[^>]*>.*?<\/a>(?:\s*\|\s*<a[^>]*>.*?<\/a>)*\s*<\/em>/gi, '');
};

export default RelatedArticlesCards;
